import { ACTION_TYPE, AppContext } from "context/appContext";
import { ChangeEvent, ReactNode, useContext, useEffect, useState } from "react";
import { Contact, Location, PanelRootContext, PhoneType, PropertyGroup, Status, TraxeroActionResponse } from "types";
import { Grid } from "@mui/material";
import { useFetcher, useLocation, useNavigate, useOutletContext } from "react-router";
import { ROUTES, routeWithParams } from "router";
import AddressAutocomplete from "components/autocompletes/AddressAutocomplete";
import { DEFAULT_BRANDING } from "context/themeContext";
import { useFetcherKey } from "hooks/useFetcherKey";
import { PropertyGroupsActionHandler } from "./PropertyGroupLayout";
import { ValidatorForm } from "react-material-ui-form-validator";
import FormFrame from "components/layout/FormFrame";
import DetailsHeader from "components/layout/DetailsHeader";
import { ScrollableFormContainer } from "components/layout/ScrollableContainer";

import { useTranslation } from "react-i18next";
import AuturaTextField from "components/inputs/AuturaTextField";
import SettingsSection from "components/layout/SettingsSection";
import AuturaPhoneField from "components/inputs/AuturaPhoneField";
import AuturaStateField from "components/inputs/AuturaStateField";
import ContactAutocomplete from "components/autocompletes/ContactAutocomplete";
import { PropertyGroupPanelContext } from "./PropertyGroupPanel";
import { getBlankContact } from "utils/emptyEntities";
import FormHeaderBar from "components/layout/FormHeaderBar";
import { ToastContext, TOAST_TYPE } from "context/toastContext";

const PropertyGroupCreate = (): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(AppContext);
  const navigate = useNavigate();
  const displayToast = useContext(ToastContext);
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [contact, setContact] = useState<Contact>(
    getBlankContact(state.tenant.id),
  );

  const [location, setLocation] = useState<Location>({
    id: 0,
    street: "",
    city: "",
    unit: "",
    stateCode: "",
    postalCode: "",
  });

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: t("buttons.create", { name: t("property_group.label", { ns: "entities" }) }),
    });
  }, []);

  const handleCancelAction = () => {
    navigate(routeWithParams(ROUTES.PROPERTY_GROUPS, [state.tenant.id]));
  };

  useEffect(() => {
    if (fetcher.data) {
      const response = fetcher.data as TraxeroActionResponse<PropertyGroupsActionHandler>;
      if (response.status === 201) {
        displayToast({
          message: t("create_success", { ns: "toast", name: t("property_group.label", { ns: "entities" }) }),
          type: TOAST_TYPE.SUCCESS,
        });
        handleCancelAction();
      } else {
        displayToast({
          message: response.message || t("create_error", { ns: "toast", name: t("property_group.label", { ns: "entities" }) }),
          type: TOAST_TYPE.ERROR,
        });
      }
    }
  }, [fetcher.data]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case "name":
        setName(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "contactName":
        setContact({ ...contact, name: value });
        break;
      case "contactEmail":
        setContact({ ...contact, primaryEmail: value });
        break;
      case "contactDescription":
        setContact({ ...contact, description: value });
        break;
      case "city":
        setLocation({ ...location, city: value });
        break;
      case "postalCode":
        setLocation({ ...location, postalCode: value });
        break;
      case "street":
        setLocation({ ...location, street: value });
        break;
      case "unit":
        setLocation({ ...location, unit: value });
        break;
    }
  };

  const handleSubmit = () => {
    const data: PropertyGroup = {
      id: 0,
      tenantID: state.tenant.id,
      name: name,
      description: description,
      status: Status.Active,
      properties: [],
      rules: [],
      rulesets: [],
      users: [],
      permitTypes: [],
      qrCodes: [],
      location: location,
      contacts: contact.name
        ? [
            {
              id: contact?.id || 0,
              name: contact?.name || "",
              description: contact?.description || "",
              status: contact?.status || Status.Active,
              primaryEmail: contact?.primaryEmail || "",
              primaryPhone: contact?.primaryPhone || "",
              primaryPhoneType: contact?.primaryPhoneType || PhoneType.Day,
              secondaryEmail: contact?.secondaryEmail || "",
              secondaryPhone: contact?.secondaryPhone || "",
              secondaryPhoneType: contact?.secondaryPhoneType || PhoneType.Day,
              website: contact?.website || "",
              tertiaryPhone: contact?.tertiaryPhone || "",
              tertiaryPhoneType: contact?.tertiaryPhoneType || PhoneType.Day,
              tenantID: state.tenant.id,
              location: {
                ...contact.location,
              },
            },
          ]
        : [],
      data: {
        settings: {
          requirePMApproval: [],
          notifyPM: [],
        },
        branding: DEFAULT_BRANDING,
      },
    };

    fetcher.submit(
      {
        handler: PropertyGroupsActionHandler.CREATE,
        body: JSON.stringify(data),
      },
      {
        method: "POST",
        encType: "application/json",
      },
    );
  };

  const handleSetLocation = (location: Location) => {
    setLocation(location);
  };

  const handleSetContact = (contact: Contact | null) => {
    if (contact) {
      setContact(contact);
    } else {
      setContact(getBlankContact(state.tenant.id));
    }
  };

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormHeaderBar />
      <FormFrame loading={fetcher.state !== "idle"}>
        <DetailsHeader
          title={t("buttons.create", {
            name: t("property_group.label", { ns: "entities" }),
          })}
          onCancel={handleCancelAction}
          submitLabel={t("buttons.create", {
            name: t("property_group.label", { ns: "entities" }),
          })}
          readonly={!state.entityPermissions.property_group.create}
          disableSubmit={fetcher.state !== "idle"}
        />
        <ScrollableFormContainer maxHeight="calc(100dvh - 326px)">
          <SettingsSection title={t("details")}>
            <Grid container width={"100%"} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name="name"
                  label={t("property_group.name", { ns: "entities" })}
                  value={name}
                  onChange={handleChange}
                  required
                  readonly={!state.entityPermissions.property_group.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name="description"
                  label={t("property_group.description", { ns: "entities" })}
                  value={description}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property_group.create}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t("property_group.contacts.label", { ns: "entities" })}
          >
            <Grid container width={"100%"} spacing={1.5}>
              <Grid size={12} sx={{ mb: 2 }}>
                <ContactAutocomplete
                  margin={"none"}
                  tenantID={state.tenant.id}
                  variant={"outlined"}
                  label={t("labels.add_contact", { ns: "properties" })}
                  setContact={handleSetContact}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name="contactName"
                  label={t("property_group.contacts.name", { ns: "entities" })}
                  value={contact.name}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property_group.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name="contactDescription"
                  label={t("property_group.contacts.description", {
                    ns: "entities",
                  })}
                  value={contact?.description || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name="contactEmail"
                  label={t("property_group.contacts.primaryEmail", {
                    ns: "entities",
                  })}
                  value={contact?.primaryEmail || ""}
                  onChange={handleChange}
                  validators={["isEmail"]}
                  readonly={!state.entityPermissions.property_group.create}
                  errorMessages={["Invalid Email"]}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaPhoneField
                  name={"primaryPhone"}
                  typeFieldName="primaryPhoneType"
                  label={t("property_group.contacts.primaryPhone", {
                    ns: "entities",
                  })}
                  phoneNumber={contact?.primaryPhone || ""}
                  setPhoneNumber={(phoneNumber: string) => {
                    setContact({ ...contact, primaryPhone: phoneNumber });
                  }}
                  phoneType={contact?.primaryPhoneType || PhoneType.Day}
                  setPhoneType={(phoneType: PhoneType) => {
                    setContact({ ...contact, primaryPhoneType: phoneType });
                  }}
                  typeFieldLabel={t(
                    "property_group.contacts.primaryPhoneType",
                    { ns: "entities" },
                  )}
                  readonly={!state.entityPermissions.property_group.create}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t("property_group.location.label", { ns: "entities" })}
            divider={false}
          >
            <Grid container width={"100%"} spacing={1.5}>
              <Grid size={12} sx={{ mb: 2 }}>
                <AddressAutocomplete
                  setLocation={handleSetLocation}
                  margin={"none"}
                  label={t("labels.lookup_location", { ns: "properties" })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name="street"
                  label={t("property_group.location.street", {
                    ns: "entities",
                  })}
                  value={location?.street || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name="unit"
                  label={t("property_group.location.unit", { ns: "entities" })}
                  value={location?.unit || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name="city"
                  label={t("property_group.location.city", { ns: "entities" })}
                  value={location?.city || ""}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property_group.create}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 1.5,
                }}
              >
                <AuturaStateField
                  label={t("property_group.location.stateCode", {
                    ns: "entities",
                  })}
                  stateCode={location?.stateCode || ""}
                  setStateCode={(stateCode: string) => {
                    setLocation({
                      ...location,
                      stateCode,
                    });
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 1.5,
                }}
              >
                <AuturaTextField
                  name="postalCode"
                  label={t("property_group.location.postalCode", {
                    ns: "entities",
                  })}
                  value={location?.postalCode || ""}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export default PropertyGroupCreate;
